import type { DateTime } from 'luxon';
import {
  generateUtmFromName,
  validateLaunchDateTimeRule,
  validateLinkRule,
  validateRequiredTextRule,
  validateUtmCampaignRule,
} from '@/lib/campaign-fields';
import {
  parseLaunchDateTime,
  type DateFormatId,
} from '@/lib/campaign-import/date-parse';

export type ImportRowField =
  | 'name'
  | 'launchDateTime'
  | 'notificationTitle'
  | 'notificationBody'
  | 'notificationLink'
  | 'utmCampaign';

/** Same codes as the backend `RowErrorCode`, minus `utmAlreadyUsed` (only the backend knows the tenant). */
export type ImportRowErrorCode =
  | 'required'
  | 'websiteDomainNotConfigured'
  | 'linkNotFromStore'
  | 'linkInvalid'
  | 'dateInvalid'
  | 'dateInPast'
  | 'dateTooFar'
  | 'timeInPast'
  | 'utmEmpty'
  | 'utmDuplicateInFile';

export interface ImportRowError {
  field: ImportRowField;
  code: ImportRowErrorCode;
}

/** A row as the grid shows and edits it: raw text, after the column mapping. */
export interface ImportRowValues {
  name: string;
  launchDate: string;
  launchTime: string;
  notificationTitle: string;
  notificationBody: string;
  notificationLink: string;
  utmCampaign: string;
  /** Separated by `|` or `;`. */
  labels: string;
}

/** A row as the backend receives it (without `rowNumber`). */
export interface CanonicalImportRow {
  name: string;
  /** ISO 8601 with offset; empty when the date could not be read. */
  launchDateTime: string;
  notificationTitle: string;
  notificationBody: string;
  notificationLink: string;
  utmCampaign: string;
  labels: string[];
}

export interface CanonicalRowContext {
  storeWebsiteDomain: string;
  now: DateTime;
}

export interface ImportRowContext extends CanonicalRowContext {
  dateFormat: DateFormatId;
  timezone: string;
}

export interface RowResult {
  row: CanonicalImportRow;
  /** Errors that depend on the row alone. */
  baseErrors: ImportRowError[];
  /** `baseErrors` plus the duplicate-in-file error. */
  errors: ImportRowError[];
}

export interface RowValidationState {
  results: RowResult[];
  /**
   * utm -> indexes of the rows with no base error that carry it, ascending. The first one owns
   * the utm and the others are duplicates, like the backend where only accepted rows claim it.
   */
  claimants: ReadonlyMap<string, readonly number[]>;
}

function splitLabels(labels: string): string[] {
  return labels
    .split(/[|;]/)
    .map((label) => label.trim())
    .filter((label) => label !== '');
}

/** Rules on the canonical row, exactly what the backend applies. */
export function validateCanonicalRow(
  row: CanonicalImportRow,
  ctx: CanonicalRowContext
): ImportRowError[] {
  const checks: [ImportRowField, ImportRowErrorCode | null][] = [
    ['name', validateRequiredTextRule(row.name)],
    ['launchDateTime', validateLaunchDateTimeRule(row.launchDateTime, ctx.now)],
    ['notificationTitle', validateRequiredTextRule(row.notificationTitle)],
    ['notificationBody', validateRequiredTextRule(row.notificationBody)],
    [
      'notificationLink',
      validateLinkRule(row.notificationLink, ctx.storeWebsiteDomain),
    ],
    ['utmCampaign', validateUtmCampaignRule(row.utmCampaign)],
  ];
  return checks.flatMap(([field, code]) => (code ? [{ field, code }] : []));
}

/** Applies the defaults (store home link, utm from the name) and reads the date. */
export function validateImportRow(
  values: ImportRowValues,
  ctx: ImportRowContext
): { row: CanonicalImportRow; errors: ImportRowError[] } {
  const domain = ctx.storeWebsiteDomain.trim();
  const link = values.notificationLink.trim();
  const utm = values.utmCampaign.trim();
  const launch = parseLaunchDateTime(
    values.launchDate,
    values.launchTime,
    ctx.dateFormat,
    ctx.timezone
  );

  const row: CanonicalImportRow = {
    name: values.name,
    launchDateTime: launch.ok ? launch.iso : '',
    notificationTitle: values.notificationTitle,
    notificationBody: values.notificationBody,
    notificationLink:
      link === '' && domain !== '' ? `https://${domain}/` : link,
    utmCampaign: utm === '' ? generateUtmFromName(values.name) : utm,
    labels: splitLabels(values.labels),
  };

  // An unreadable date has no ISO to check: keep the parse reason instead of "required".
  const errors = validateCanonicalRow(row, ctx).map((error) =>
    !launch.ok && error.field === 'launchDateTime'
      ? { ...error, code: launch.code }
      : error
  );
  return { row, errors };
}

function toResult(values: ImportRowValues, ctx: ImportRowContext): RowResult {
  const { row, errors } = validateImportRow(values, ctx);
  return { row, baseErrors: errors, errors };
}

function withDuplicateRule(
  result: RowResult,
  index: number,
  claimants: ReadonlyMap<string, readonly number[]>
): RowResult {
  const isDuplicate =
    result.baseErrors.length === 0 &&
    claimants.get(result.row.utmCampaign)?.[0] !== index;
  const errors: ImportRowError[] = isDuplicate
    ? [{ field: 'utmCampaign', code: 'utmDuplicateInFile' }]
    : result.baseErrors;

  const unchanged =
    errors.length === result.errors.length &&
    errors.every((error, i) => error === result.errors[i]);
  return unchanged ? result : { ...result, errors };
}

export function validateImportRows(
  rows: ImportRowValues[],
  ctx: ImportRowContext
): RowValidationState {
  const base = rows.map((values) => toResult(values, ctx));

  const claimants = new Map<string, number[]>();
  base.forEach((result, index) => {
    if (result.baseErrors.length > 0) return;
    const list = claimants.get(result.row.utmCampaign);
    if (list) list.push(index);
    else claimants.set(result.row.utmCampaign, [index]);
  });

  return {
    results: base.map((result, index) =>
      withDuplicateRule(result, index, claimants)
    ),
    claimants,
  };
}

function withoutIndex(list: readonly number[] | undefined, index: number) {
  return (list ?? []).filter((item) => item !== index);
}

function withIndex(list: readonly number[] | undefined, index: number) {
  return [...(list ?? []), index].sort((a, b) => a - b);
}

/**
 * Revalidates one edited row and only the rows that share its old or new utm (the one that
 * owns a utm may change). The other results keep their identity.
 */
export function revalidateImportRow(
  state: RowValidationState,
  index: number,
  values: ImportRowValues,
  ctx: ImportRowContext
): RowValidationState {
  const previous = state.results[index];
  const next = toResult(values, ctx);

  const claimants = new Map(state.claimants);
  const touchedUtms = new Set<string>();

  if (previous.baseErrors.length === 0) {
    const utm = previous.row.utmCampaign;
    claimants.set(utm, withoutIndex(claimants.get(utm), index));
    touchedUtms.add(utm);
  }
  if (next.baseErrors.length === 0) {
    const utm = next.row.utmCampaign;
    claimants.set(utm, withIndex(claimants.get(utm), index));
    touchedUtms.add(utm);
  }
  for (const utm of touchedUtms) {
    if (claimants.get(utm)?.length === 0) claimants.delete(utm);
  }

  const results = [...state.results];
  results[index] = withDuplicateRule(next, index, claimants);
  for (const utm of touchedUtms) {
    for (const other of claimants.get(utm) ?? []) {
      if (other !== index) {
        results[other] = withDuplicateRule(results[other], other, claimants);
      }
    }
  }

  return { results, claimants };
}
